/**
 * resultToDashboard
 *
 * Maps a reconciliation `result.json` (`{ results: AlgorithmResultEnvelope[] }`)
 * into a dashboard-kit `DashboardConfig`. The mapping is *generic*: each
 * algorithm declares its charts in `config/chartIntent.ts` (presentation) over
 * the Lib output schema (`config/outputs.ts`, the contract). This file only
 * interprets the small set of `layout` kinds — adding a chart for an algorithm
 * whose shape fits an existing layout needs no code here, just an intent entry.
 *
 * An algorithm with no chart intent yields no charts — the dashboard surface
 * falls back to a raw-JSON view so the data stays inspectable.
 */
import { colorAt, type ChartConfig, type DashboardConfig, type DataPoint } from '@micronet/dashboard-kit';
import type {
  AlgorithmResultEnvelope,
  ResultJson,
  Stats,
  ScatterResult,
  ModelStats,
} from '../types/results';
import type { BatchRequest, AlgorithmEntry, PlotMeasure } from '../types/compute';
import { resolveErrorBases } from '../types/compute';
import { currentFigureTitle } from '../config/catalogOverlay';
import {
  GRADE_UNIT,
  MEASURE_LABELS,
  MEASURE_SERIES_LABELS,
  measureUnit,
} from '../config/measures';
import {
  CHART_INTENT,
  COLOR,
  RECONCILIATION_CLASSES,
  type ChartIntent,
  type ModelsDualAxisChart,
  type PairedArraysChart,
  type XYPointsChart,
  type SpatialMapChart,
  type IndexedVectorsChart,
} from '../config/chartIntent';
import { executedInstanceIds, positionalInstanceId } from './algorithmInstance';
import { liftModelFilters } from './batchRequestBuilder';
import { isPeriodLeafId, periodOrFilterId } from './filterSpec';
import { periodColumnPosition, selectedPeriods } from './periodFilter';
import { executedSplitCategory } from './tonnageMode';
import { executedErrorCutoff, isErrorCutoffFilterId, selectorWithoutCutoff } from './errorCutoff';
import { describeFilter } from './filterLabel';

/**
 * What a builder returns. The chart id is deliberately NOT theirs to set: it is
 * stamped once, in `algorithmResultToCharts`, from the identity of the configured
 * chart the envelope came from. Six builders each spelling their own id is how it
 * came to be `${algorithmName}-${idSuffix}` — a string that moves when the
 * algorithm is renamed. The type is what stops a builder from WRITING one: the
 * excess-property check rejects `id` in a returned literal, which is how all six
 * of them return. The `id?: never` closes the other half — an `id` arriving
 * through a spread of a wider object — which the excess-property check alone does
 * not see, and which used to rest on the stamp putting `id` LAST in its spread.
 * Both halves are now the compiler's, so the stamp is the only place one is written.
 */
type IdlessChart = Omit<ChartConfig, 'id'> & { id?: never };

// Per-model summary block for the histogram stats box: the model name is the
// `label` (dashboard-kit's stats box renders it as a bold header), then one stat
// per line. Percentiles are p5/p50/p95 — what the Lib's Stats emits — not
// p25/p75 quartiles.
function statsRow(label: string, s: Stats): { label: string; value: string } {
  return {
    label,
    value: [
      `N=${s.count}`,
      `μ=${fmt(s.mean)}`,
      `σ=${fmt(s.std)}`,
      `máx=${fmt(s.max)}`,
      `P95=${fmt(s.p95)}`,
      `mediana=${fmt(s.p50)}`,
      `P5=${fmt(s.p5)}`,
      `mín=${fmt(s.min)}`,
    ].join('<br>'),
  };
}

// The three measures the tonnage algorithms emit; the user picks ≥1 to plot
// (`plot_measures`). Each maps to its Lib output field + display labels.
type MeasureMeta = { measure: PlotMeasure; field: string; label: string; axisLabel: string };
// Labels come from measures.ts (the single source of the measure vocabulary);
// only the Lib output field is owned here.
const MEASURE_META: Record<PlotMeasure, MeasureMeta> = {
  tonnage: { measure: 'tonnage', field: 'totalTonnages', label: MEASURE_LABELS.tonnage, axisLabel: MEASURE_LABELS.tonnage },
  metal: { measure: 'metal', field: 'totalMetals', label: MEASURE_LABELS.metal, axisLabel: MEASURE_LABELS.metal },
  grade: { measure: 'grade', field: 'totalGrades', label: MEASURE_LABELS.grade, axisLabel: MEASURE_LABELS.grade },
};
// Dual-axis (2 measures): the extensive measure goes on the left axis → prefer
// tonnage, then metal, then grade. A single measure reuses this order (it is
// trivially the only one) and draws on the left axis alone. Stacked panels (3 measures) are ordered
// top→bottom to match the client mockup (Metal / Tonelaje / Ley).
const DUAL_AXIS_ORDER: PlotMeasure[] = ['tonnage', 'metal', 'grade'];
const PANEL_ORDER: PlotMeasure[] = ['metal', 'tonnage', 'grade'];

// The user's measure selection (≥1), or null when absent/empty (older configs /
// no selection) → the dual-axis falls back to the intent's hardcoded pair.
function selectedMeasures(algo?: AlgorithmEntry): PlotMeasure[] | null {
  const sel = algo?.plot_measures;
  if (!Array.isArray(sel) || sel.length < 1) return null;
  return sel;
}

// Order a selection by a fixed sequence and map to display metadata.
function orderedMeta(order: PlotMeasure[], sel: PlotMeasure[]): MeasureMeta[] {
  return order.filter((m) => sel.includes(m)).map((m) => MEASURE_META[m]);
}

// The by-cutoff tonnage curve gets mockup-styled labels; the by-categories
// variant (shares the intent) keeps its plain labels. LP = model 0
// (grade_columns_index_1), CP = model 1 (grade_columns_index_2).
const CUTOFF_ALGORITHM = 'tonnage_grade_metal_by_cutoff';
const CATEGORIES_ALGORITHM = 'tonnage_grade_metal_by_categories';
const GRADE_COL_PARAM = ['grade_columns_index_1', 'grade_columns_index_2'] as const;
const CATEGORY_COL_PARAM = ['category_column_index_1', 'category_column_index_2'] as const;

// "Categoría de interés" (US 2203): an optional per-model categorical filter
// (LP/CP each resolved independently — see lib/periodFilter) layered on top of
// whatever the chart's own algorithm needs, same shape regardless of which
// tonnage variant (by-cutoff or by-categories) is showing. `algo.filters` /
// `model_filters` here are the LOWERED wire pool (what actually ran), so
// `liftModelFilters` reconstructs each model's own per-model `FilterSpec[]`
// (the draft shape) before periodFilter's helpers can read it — same
// reconstruction `batchRequestToDraft` uses to redraw the edit form.
function categoryOfInterestLabel(algo: AlgorithmEntry | undefined): string | undefined {
  if (!algo) return undefined;
  const cardsFor = (m: number) => liftModelFilters(algo, m);
  const perModel = [0, 1].map((m) => {
    const pos = periodColumnPosition(cardsFor(m), m);
    const name = pos != null ? algo.models?.[m]?.columns?.[pos]?.name : undefined;
    return { name, values: selectedPeriods(cardsFor(m), m) };
  });
  const [lp, cp] = perModel;
  if (lp.values.length === 0 && cp.values.length === 0) return undefined;

  const sortValues = (vs: string[]) =>
    [...vs].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const label = (m: { name?: string; values: string[] }) =>
    `${m.name ?? '—'}: ${sortValues(m.values).join(', ')}`;

  // Only one model is actually filtered on a period column → nothing to
  // conflict with, print its selection plain.
  if (lp.values.length === 0) return label(cp);
  if (cp.values.length === 0) return label(lp);

  // Both filtered: an identical selection on both models still reads as one
  // label. A diverging column name or value set must say WHICH model got
  // which — this is a reconciliation, so "which rows went into each side" is
  // exactly what a reader must not be misled about (see 'Variables: … (CP) ·
  // … (LP)' above for the same LP/CP split).
  const sortedLp = sortValues(lp.values);
  const sortedCp = sortValues(cp.values);
  const same =
    lp.name === cp.name &&
    sortedLp.length === sortedCp.length &&
    sortedLp.every((v, i) => v === sortedCp[i]);
  if (same) return label(lp);
  return `LP ${label(lp)} · CP ${label(cp)}`;
}

// Title, subtitle (real columns + filter), X = "Cutoff (%)", and Y/panel labels
// "<measure> sobre cutoff (<unit>)" — the mass unit (t/Mt) is driven by the
// algorithm's `return_m_tonnage` flag. `measures` are the plotted measures
// (short labels feed the title, series labels feed the axes/legend).
function cutoffLabels(algo: AlgorithmEntry | undefined) {
  const rmt = algo?.parameters?.return_m_tonnage;
  const lpCol = comparedColumnName(algo, 0, GRADE_COL_PARAM[0]);
  const cpCol = comparedColumnName(algo, 1, GRADE_COL_PARAM[1]);
  const colOf = (modelIdx: number) => (modelIdx === 0 ? lpCol : cpCol) ?? '—';
  const parts = [`Variables: ${cpCol ?? '—'} (CP) · ${lpCol ?? '—'} (LP)`];
  const filter = appliedFilterLabel(algo);
  if (filter) parts.push(`filtro: ${filter}`);
  const categoryOfInterest = categoryOfInterestLabel(algo);
  if (categoryOfInterest) parts.push(categoryOfInterest);
  return {
    // Title names the compared variables (grade columns), consistent with the
    // by-categories chart; the plotted measures read from the axes/legend.
    title: `Distribución ${cpCol ?? '—'}-${lpCol ?? '—'} (CP vs LP)`,
    subtitle: parts.join(' · '),
    xAxisLabel: `Cutoff (${GRADE_UNIT})`,
    axisLabel: (me: MeasureMeta) =>
      `${MEASURE_SERIES_LABELS[me.measure]} (${measureUnit(me.measure, rmt)})`,
    // Dual-axis legend entry: "<serieLabel> <Model> (<colModel>)".
    seriesName: (modelIdx: number, me: MeasureMeta) =>
      `${MEASURE_SERIES_LABELS[me.measure]} ${modelIdx === 0 ? 'LP' : 'CP'} (${colOf(modelIdx)})`,
  };
}

// Labels for the by-CATEGORIES tonnage chart (line+bar): title over the compared
// grade columns, subtitle with the columns + fixed cut-off, X = the category
// column name (fallback "Categoría"), Y/Y2 with units. Same shape as cutoffLabels
// so buildModelsDualAxis consumes it identically. LP = model 0, CP = model 1.
function categoryLabels(algo: AlgorithmEntry | undefined) {
  const rmt = algo?.parameters?.return_m_tonnage;
  const lpCol = comparedColumnName(algo, 0, GRADE_COL_PARAM[0]);
  const cpCol = comparedColumnName(algo, 1, GRADE_COL_PARAM[1]);
  const colOf = (modelIdx: number) => (modelIdx === 0 ? lpCol : cpCol) ?? '—';
  const catCol =
    comparedColumnName(algo, 0, CATEGORY_COL_PARAM[0]) ??
    comparedColumnName(algo, 1, CATEGORY_COL_PARAM[1]);
  const cutoff = algo?.parameters?.cutoff;
  const parts = [`Variables: ${cpCol ?? '—'} (CP) · ${lpCol ?? '—'} (LP)`];
  if (typeof cutoff === 'number') parts.push(`corte: ${cutoff} ${GRADE_UNIT}`);
  if (catCol) parts.push(`categoría: ${catCol}`);
  const filter = appliedFilterLabel(algo);
  if (filter) parts.push(`filtro: ${filter}`);
  const categoryOfInterest = categoryOfInterestLabel(algo);
  if (categoryOfInterest) parts.push(categoryOfInterest);
  return {
    title: `Distribución ${cpCol ?? '—'}-${lpCol ?? '—'} (CP vs LP)`,
    subtitle: parts.join(' · '),
    // X is the geological category: label the axis with the category column name
    // (falls back to the generic word when the column can't be resolved).
    xAxisLabel: catCol ? `Categoría (${catCol})` : 'Categoría',
    axisLabel: (me: MeasureMeta) =>
      `${MEASURE_SERIES_LABELS[me.measure]} (${measureUnit(me.measure, rmt)})`,
    seriesName: (modelIdx: number, me: MeasureMeta) =>
      `${MEASURE_SERIES_LABELS[me.measure]} ${modelIdx === 0 ? 'LP' : 'CP'} (${colOf(modelIdx)})`,
  };
}

/**
 * Sort built points by their numeric x, ascending, so a line over a cut-off list
 * typed out of order never backtracks (Bug 2801). Each point already carries its
 * own values, so every cut-off stays with its numbers; the entered list itself
 * is left as typed. Array.prototype.sort is stable, so equal cut-offs keep their
 * entry order and a non-finite x (never expected) goes last instead of dropped.
 */
function sortByX(data: DataPoint[], xKey: string): void {
  const key = (p: DataPoint) => {
    const x = p[xKey];
    return typeof x === 'number' && Number.isFinite(x) ? x : Infinity;
  };
  data.sort((a, b) => key(a) - key(b));
}

// One row carrying parallel arrays on a shared x array → one chart, one series
// per array (e.g. histogram counts1/counts2 over xAxis), bin-by-bin.
/** Round to 3 decimals (metres) to shed float noise from plotted coordinates. */
const round3 = (n: number): number => Number(n.toFixed(3));

function buildPairedArrays(
  intent: PairedArraysChart,
  rows: unknown[],
  algo?: AlgorithmEntry
): IdlessChart | null {
  const row = rows[0] as Record<string, unknown> | undefined;
  if (!row) return null;

  const edges =
    ((row[intent.xField] ?? (intent.xFallback ? row[intent.xFallback] : undefined)) as number[]) ??
    [];
  const series = intent.series.map((s) => ({ ...s, values: (row[s.field] as number[]) ?? [] }));
  const binCount = Math.min(edges.length, ...series.map((s) => s.values.length));

  const data: DataPoint[] = [];
  for (let i = 0; i < binCount; i++) {
    const point: DataPoint = { [intent.xKey]: round3(edges[i]) };
    for (const s of series) point[s.label] = s.values[i] ?? 0;
    data.push(point);
  }

  return {
    type: intent.type,
    title: intent.title,
    // Resolved "Variables: … (CP) · … (LP) · filtro: …" when the algorithm
    // declares compared columns; else the static intent subtitle.
    subtitle: comparedSubtitle(algo, intent.comparedColumns) ?? intent.subtitle,
    data,
    encoding: { xKey: intent.xKey },
    xAxisLabel: intent.xAxisLabel,
    yAxisLabel: intent.yAxisLabel,
    series: intent.series.map((s) => ({
      dataKey: s.label,
      name: s.label,
      color: COLOR[s.color],
      type: intent.type === 'bar' ? 'bar' : 'line',
    })),
    options: intent.options,
    statistics: intent.stats?.map((st) => statsRow(st.label, row[st.field] as Stats)),
  };
}

// data[] rows are the compared models, each carrying two measure vectors per
// position → one dual-axis chart. Encoding: color = model, dash = measure,
// axis = measure (primary → left, secondary → right). One series per
// (model × measure); the legend key combines the model and measure labels.
function buildModelsDualAxis(
  algorithmName: string,
  intent: ModelsDualAxisChart,
  rows: unknown[],
  algo?: AlgorithmEntry,
  measures?: MeasureMeta[] | null
): IdlessChart | null {
  const models = intent.models.filter((_, i) => i < rows.length);
  if (models.length === 0) return null;
  const row = (i: number) => rows[i] as Record<string, number[] | undefined>;
  // The user's selection wins (primary → left/solid, secondary → right/dashed);
  // otherwise fall back to the default pair (tonnage + grade). A single measure
  // (SPEC/2515) has no secondary: one series per model on the left axis only.
  const pair: MeasureMeta[] =
    measures && measures.length >= 1 ? measures.slice(0, 2) : [MEASURE_META.tonnage, MEASURE_META.grade];
  const primary = { ...pair[0], dash: 'solid' as const };
  const secondary = pair[1] ? { ...pair[1], dash: 'dash' as const } : null;
  // Resolved labels (columns/units) for the tonnage charts; other algorithms
  // keep their static intent labels.
  const L =
    algorithmName === CUTOFF_ALGORITHM
      ? cutoffLabels(algo)
      : algorithmName === CATEGORIES_ALGORITHM
        ? categoryLabels(algo)
        : null;
  // The primary measure drives the x extent; every plotted measure shares the
  // cut-off index.
  const n = row(0)[primary.field]?.length ?? 0;
  const key = (modelLabel: string, measureLabel: string) => `${modelLabel} ${measureLabel}`;

  // Real x values come from the entered config parameter (e.g. the cut-off
  // grades), which the result vectors don't carry. Fall back to the 1-based
  // position when the parameter is absent or shorter than the measure vectors.
  //
  // The values come from the LIVE config while the numbers come from the STORED
  // run, and a config can be edited and saved without re-executing — so the list
  // is only trusted when it still has exactly one entry per plotted point. A
  // different length proves they are out of step; a longer list would otherwise
  // silently label the run with its first n entries.
  const xParamValue = intent.xParam ? algo?.parameters?.[intent.xParam] : undefined;
  const xValues =
    Array.isArray(xParamValue) && xParamValue.length === n ? (xParamValue as number[]) : undefined;

  const data: DataPoint[] = [];
  for (let i = 0; i < n; i++) {
    const point: DataPoint = { [intent.xKey]: xValues ? xValues[i] : i + 1 };
    models.forEach((m, mi) => {
      point[key(m.label, primary.label)] = row(mi)[primary.field]?.[i] ?? 0;
      if (secondary) point[key(m.label, secondary.label)] = row(mi)[secondary.field]?.[i] ?? 0;
    });
    data.push(point);
  }
  if (intent.sortByX && xValues) sortByX(data, intent.xKey);

  // Series grouped by measure so the legend reads as two blocks (tonnage, then
  // grade). Primary stays on the left axis (yAxis omitted); secondary → right.
  // The per-series `type` (bar/line) is what `lineBar` reads to draw the primary
  // as bars and the secondary as a line — the by-categories default, and what
  // the by-cutoff curve becomes if the user swaps its type. `line`/`bar` ignore
  // it and render every series the same way (solid vs dashed).
  //
  // `markers`/`smooth` only when this intent renders as a curve (`type:
  // 'line'`, the by-cutoff variant): a dot at each entered cut-off grade, on
  // a smoothed spline (US 2446 follow-up). The by-categories variant (`type:
  // 'lineBar'`) draws tonnage as bars, where neither applies.
  const markers = intent.type === 'line';
  const smooth = markers;
  const series = [
    ...models.map((m, mi) => ({
      dataKey: key(m.label, primary.label),
      name: L ? L.seriesName(mi, primary) : key(m.label, primary.label),
      color: COLOR[m.color],
      type: 'bar' as const,
      dash: primary.dash,
      markers,
      smooth,
    })),
    ...(secondary
      ? models.map((m, mi) => ({
          dataKey: key(m.label, secondary.label),
          name: L ? L.seriesName(mi, secondary) : key(m.label, secondary.label),
          color: COLOR[m.color],
          type: 'line' as const,
          dash: secondary.dash,
          yAxis: 'right' as const,
          markers,
          smooth,
        }))
      : []),
  ];

  return {
    type: intent.type,
    title: L ? L.title : intent.title,
    ...(L ? { subtitle: L.subtitle } : {}),
    encoding: { xKey: intent.xKey },
    xAxisLabel: L ? L.xAxisLabel : intent.xAxisLabel,
    yAxisLabel: L ? L.axisLabel(primary) : primary.axisLabel,
    ...(secondary ? { y2AxisLabel: L ? L.axisLabel(secondary) : secondary.axisLabel } : {}),
    data,
    series,
    options: intent.options,
  };
}

// Three measures selected → small-multiples: one stacked panel per measure
// (Metal / Tonelaje / Ley by priority), each with its own Y axis over a shared
// X. Within a panel, color = the panel/measure (so Metal, Tonelaje and Ley
// each read as a distinct color) and dash distinguishes LP (solid) from CP
// (dashed) — LP also carries a point marker at every value, CP doesn't.
// Renders via dashboard-kit's `stackedPanels` chart.
function buildStackedPanels(
  algorithmName: string,
  intent: ModelsDualAxisChart,
  rows: unknown[],
  algo: AlgorithmEntry | undefined,
  measures: MeasureMeta[]
): IdlessChart | null {
  const models = intent.models.filter((_, i) => i < rows.length);
  if (models.length === 0) return null;
  const row = (i: number) => rows[i] as Record<string, number[] | undefined>;
  const n = row(0)[measures[0].field]?.length ?? 0;
  const key = (modelLabel: string, measureLabel: string) => `${modelLabel} ${measureLabel}`;

  const xParamValue = intent.xParam ? algo?.parameters?.[intent.xParam] : undefined;
  const xValues =
    Array.isArray(xParamValue) && xParamValue.length >= n ? (xParamValue as number[]) : undefined;

  const data: DataPoint[] = [];
  for (let i = 0; i < n; i++) {
    const point: DataPoint = { [intent.xKey]: xValues ? xValues[i] : i + 1 };
    measures.forEach((me) =>
      models.forEach((m, mi) => {
        point[key(m.label, me.label)] = row(mi)[me.field]?.[i] ?? 0;
      })
    );
    data.push(point);
  }
  if (intent.sortByX && xValues) sortByX(data, intent.xKey);

  const L = algorithmName === CUTOFF_ALGORITHM ? cutoffLabels(algo) : null;
  // No `legendGroup`: each panel×model series keeps its own legend entry.
  // Collapsing LP/CP into one shared entry per model only made sense while
  // color was constant per model (the same swatch, repeated once per panel);
  // now color encodes the panel/measure, so a shared entry would show only
  // the first panel's color for series that actually render in 3 different
  // colors — misleading. 6 distinct entries (one per panel×model) instead,
  // matching the reference image.
  const series = measures.flatMap((me, panel) =>
    models.map((m, mi) => ({
      dataKey: key(m.label, me.label),
      name: key(m.label, me.label),
      color: colorAt(panel),
      type: 'line' as const,
      panel,
      dash: mi === 0 ? ('solid' as const) : ('dash' as const),
      markers: mi === 0,
    }))
  );

  return {
    type: 'stackedPanels',
    title: L ? L.title : intent.title,
    ...(L ? { subtitle: L.subtitle } : {}),
    encoding: { xKey: intent.xKey },
    xAxisLabel: L ? L.xAxisLabel : intent.xAxisLabel,
    data,
    series,
    options: {
      ...intent.options,
      panels: measures.map((me) => ({ label: L ? L.axisLabel(me) : me.axisLabel })),
    },
  };
}

// Plain name of the compared column for a model (e.g. "cut_lp"). The parameter
// `paramName` indexes the model's columns[] by position.
function comparedColumnName(
  algo: AlgorithmEntry | undefined,
  modelIdx: number,
  paramName: string
): string | undefined {
  const pos = algo?.parameters?.[paramName];
  if (typeof pos !== 'number') return undefined;
  return algo?.models?.[modelIdx]?.columns?.[pos]?.name;
}

// Shared chart subtitle: names the compared LP/CP columns, any extra context
// and the applied filter, in the same shape the tonnage charts use so every plot
// reads consistently:
//   "Variables: <cp> (CP) · <lp> (LP) · filtro: <…>"
// Returns undefined when nothing resolves (no columns, no filter).
function comparedSubtitle(
  algo: AlgorithmEntry | undefined,
  compared: [string, string] | undefined
): string | undefined {
  const parts: string[] = [];
  if (compared) {
    const lp = comparedColumnName(algo, 0, compared[0]);
    const cp = comparedColumnName(algo, 1, compared[1]);
    if (lp || cp) parts.push(`Variables: ${cp ?? '—'} (CP) · ${lp ?? '—'} (LP)`);
  }
  // The `error` cut-off runs as a filter (lib/errorCutoff), but it is a parameter
  // of the figure, so it reads as one — never as its marker id.
  const cutoff = algo ? executedErrorCutoff(algo) : undefined;
  if (cutoff !== undefined) parts.push(`ley de corte ≥ ${cutoff}`);
  const filter = appliedFilterLabel(algo);
  if (filter) parts.push(`filtro: ${filter}`);
  return parts.length ? parts.join(' · ') : undefined;
}

// Best-effort descriptor of any applied filter (no human label exists in the
// contract, so it is described from the filter pool + model columns — see
// lib/filterLabel — and falls back to the filter id + type when it can't be).
// Empty when no model is filtered.
// Skips a model's slot when it resolves to exactly its own "categoría de
// interés" selection (US 2203) — that filter already gets its own dedicated,
// readable "columna: v1, v2, …" segment via `categoryOfInterestLabel`, so
// showing it again here as e.g. "m0periodOr (combined)" would be pure noise.
// With 2+ values selected that selector is the OR-group id; with exactly ONE
// value, `withAutoCombined` collapses the group away and the selector IS the
// single period leaf itself (`isPeriodLeafId`) — both must be skipped, or a
// single pick showed up twice: once here as "m0period1 (continuous)" (a
// numeric-valued leaf lowers to `continuous`, see periodFilter.ts), once in
// the clean label.
function appliedFilterLabel(algo: AlgorithmEntry | undefined): string | undefined {
  const modelFilters = algo?.model_filters ?? [];
  const defs = algo?.filters ?? [];
  const labels = modelFilters.map((selector, m) => {
    const id = selector && selectorWithoutCutoff(selector, defs, m);
    if (!id || id === periodOrFilterId(m) || isPeriodLeafId(id, m)) return undefined;
    // A lone cut-off marker is the model's selector; it has its own segment.
    if (isErrorCutoffFilterId(id)) return undefined;
    const def = defs.find((f) => f.id === id);
    // A categorical filter reads best by its value (e.g. the scenario-3 per-category
    // tonnage fan-out labels each chart with its category); others read as what
    // they keep (`ugcut_cp = 5, 6`, Bug 2754) and fall back to `id (type)` only
    // when that cannot be described.
    if (def?.type === 'categorical' && def.category_value !== '') return String(def.category_value);
    const described = describeFilter(id, defs, algo?.models?.[m]?.columns, m);
    if (described !== undefined) return described;
    return def?.type ? `${id} (${def.type})` : id;
  });
  const [lp, cp] = labels;
  // Both models filtered differently: say WHICH model got which — the filter's id
  // used to carry it (`m0…`/`m1…`), a description does not (same LP/CP split as
  // categoryOfInterestLabel, `LP … · CP …`). ` · ` between them: a description
  // already uses `, ` (OR) and `; ` (AND) inside (`ugcut_cp = 5, 6; mes = 8`).
  if (lp !== undefined && cp !== undefined && lp !== cp) return `LP ${lp} · CP ${cp}`;
  return lp ?? cp;
}

// Tolerant of malformed wrapper output: a missing/NaN field renders as "—"
// instead of throwing on `.toFixed`.
const fmt = (n: unknown): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toFixed(3) : '—';
/**
 * The sections the scatter's statistics are drawn under, in this order.
 *
 * The names live HERE and not in dashboard-kit: they are conciliatron's reading of
 * the numbers (what the run measured, how each variable is distributed, how blocks
 * moved between classes), and the kit renders whatever groups it is handed without
 * knowing any of them.
 */
const SCATTER_STAT_GROUPS = {
  analysis: 'ANÁLISIS',
  distribution: 'DISTRIBUCIÓN',
  transitions: 'TRANSICIONES',
} as const;

/**
 * One row per statistic, never a packed line.
 *
 * This replaces a `modelStatsLine` helper that packed μ/σ/min/max into a single
 * value string. The gutter could only draw that as one long run: too wide for its
 * column, so it wrapped and left `max=…` orphaned on the next line, out of line
 * with its own label. Four atomic rows let every value sit right-aligned in the
 * value column.
 */
function modelStatsRows(
  prefix: string,
  s: ModelStats
): { label: string; value: string | number; group: string }[] {
  const group = SCATTER_STAT_GROUPS.distribution;
  return [
    { label: `${prefix} μ`, value: fmt(s.mean), group },
    { label: `${prefix} σ`, value: fmt(s.std), group },
    { label: `${prefix} min`, value: fmt(s.min), group },
    { label: `${prefix} max`, value: fmt(s.max), group },
  ];
}

// Stats box for the scatter: N, per-variable stats, regression, % per class.
function scatterStatistics(
  row: Record<string, unknown> | undefined,
  xLabel: string,
  yLabel: string
): { label: string; value: string | number; group?: string }[] {
  if (!row) return [];
  const out: { label: string; value: string | number; group?: string }[] = [];
  const sm = row.statsModel as ModelStats[] | undefined;
  const lr = row.linealRegression as ScatterResult['linealRegression'] | undefined;
  const counts = row.countTypeScatter as Record<string, number> | undefined;

  const analysis = SCATTER_STAT_GROUPS.analysis;
  if (typeof sm?.[0]?.count === 'number')
    out.push({ label: 'N DATA', value: sm[0].count, group: analysis });
  if (lr) {
    // The Lib regresses CP (model 1) on LP (model 0), but the scatter plots CP on
    // X and LP on Y (SPEC/2201). State the regression of the plotted axes — LP on
    // CP, as Desktop does — from the Lib's own full-population stats:
    // b = r·σLP/σCP, a = μLP − b·μCP. Not OLS over the tuples: the Lib keys them
    // by (LP, CP) pair, so repeated pairs collapse (SPEC/2872).
    const slope =
      typeof lr.correlation === 'number' && sm?.[0] && sm?.[1]
        ? (lr.correlation * sm[0].std) / sm[1].std
        : undefined;
    const intercept = slope !== undefined ? sm![0].mean - slope * sm![1].mean : undefined;
    out.push({ label: 'Correlación', value: fmt(lr.correlation), group: analysis });
    out.push({ label: 'Pendiente', value: fmt(slope), group: analysis });
    out.push({ label: 'Intercepto', value: fmt(intercept), group: analysis });
  }
  // The cut-off is NOT repeated here: it already travels in the subtitle
  // ("… · Ley de corte: 0.5"), and a stats column short of rows is worth more than
  // a number printed twice in the same figure.
  if (sm?.[0]) out.push(...modelStatsRows(xLabel, sm[0]));
  if (sm?.[1]) out.push(...modelStatsRows(yLabel, sm[1]));
  if (counts) {
    for (const code of [1, 2, 3, 4]) {
      const pct = counts[String(code)];
      if (typeof pct !== 'number') continue;
      // Short code only (e.g. "O2O") — the full label lives in the legend.
      const short = RECONCILIATION_CLASSES[code]?.label.split(' ')[0] ?? String(code);
      out.push({ label: short, value: `${pct.toFixed(1)}%`, group: SCATTER_STAT_GROUPS.transitions });
    }
  }
  return out;
}

// data[0] carries a tuple[] of [x, y, class?] → one scatter chart (encoding.xKey/yKey).
// When `intent.classKey` is set, t[2] is mapped to a reconciliation-class label and
// drives `groupKey` so points color by class. The algorithm entry supplies the
// compared column names (axis labels + subtitle) and the cutoff (cut-off cross-hair).
function buildXYPoints(
  intent: XYPointsChart,
  rows: unknown[],
  algo?: AlgorithmEntry
): IdlessChart | null {
  const row = rows[0] as Record<string, unknown> | undefined;
  const tuples = (row?.[intent.field] as number[][]) ?? [];
  if (tuples.length === 0) return null;

  // Plotted X = model 1 / CP, plotted Y = model 0 / LP — flipped from the
  // compared-column order (t[0]=LP, t[1]=CP) per the axis-inversion bug
  // report (SPEC/2201). `xRole`/`yRole` below stay tied to the MODEL, not the
  // plotted axis, so the subtitle and stats-box grouping (keyed off them) read
  // the same regardless of which axis a model's values land on.
  const data: DataPoint[] = tuples.map((t) => {
    const point: DataPoint = { [intent.xKey]: t[1], [intent.yKey]: t[0] };
    if (intent.classKey) {
      point[intent.classKey] = RECONCILIATION_CLASSES[t[2]]?.label ?? String(t[2]);
    }
    return point;
  });

  const cutoff =
    typeof algo?.parameters?.cutoff === 'number' ? (algo.parameters.cutoff as number) : undefined;

  // Compared column names (model 0 / LP, model 1 / CP). The subtitle and stats
  // box keep the "-LP"/"-CP" role suffix as context (which variable belongs to
  // which model), independent of which axis that model is plotted on below.
  const xName = comparedColumnName(algo, 0, 'variable_comparison_column_1');
  const yName = comparedColumnName(algo, 1, 'variable_comparison_column_2');
  // Both axes plot grades, so the resolved column name carries the grade unit —
  // an axis reading "cut_lp" alone leaves a printed figure's numbers unitless.
  const xAxis = xName ? `${xName} (${GRADE_UNIT})` : (intent.xAxisLabel ?? 'LP');
  const yAxis = yName ? `${yName} (${GRADE_UNIT})` : (intent.yAxisLabel ?? 'CP');
  const xRole = xName ? `${xName}-LP` : xAxis;
  const yRole = yName ? `${yName}-CP` : yAxis;

  const subtitleParts = [`Variables: ${yRole} v/s ${xRole}`];
  if (cutoff !== undefined) subtitleParts.push(`Ley de corte: ${cutoff}`);
  const filter = appliedFilterLabel(algo);
  if (filter) subtitleParts.push(`filtro: ${filter}`);

  const statistics = scatterStatistics(row, xRole, yRole);

  return {
    type: intent.type,
    title: intent.title,
    subtitle: subtitleParts.join(' · '),
    data,
    encoding: {
      xKey: intent.xKey,
      yKey: intent.yKey,
      ...(intent.classKey ? { groupKey: intent.classKey } : {}),
    },
    ...(intent.groupColors ? { groupColors: intent.groupColors } : {}),
    // Swapped with the plotted data above: the X axis now carries the CP name,
    // the Y axis the LP name.
    xAxisLabel: yAxis,
    yAxisLabel: xAxis,
    ...(cutoff !== undefined
      ? { limits: { vlines: [{ value: cutoff }], hlines: [{ value: cutoff }] } }
      : {}),
    ...(statistics.length ? { statistics } : {}),
    options: intent.options,
  };
}

/** The shared block grid the wrapper reconciles on: `delta` (block size) and
 * `min` (the grid's min-CORNER origin). Lets the frontend turn a block's integer
 * grid index back into its real centroid — the Lib emits the spatial maps keyed
 * by integer `i,j,k` (`AlgorithmRegistry.cpp`), not coordinates, so this is
 * where the georeference is recovered. */
export interface BlockGrid {
  delta: [number, number, number];
  min: [number, number, number];
}

/** Reconstruct the shared grid from the executed config's readers, mirroring the
 * worker's `compute.LowerGrid` (Go): one shared `delta` (all readers must match)
 * and `min` as the element-wise min over the readers that declare a corner.
 * Returns null when the grid can't be rebuilt — no readers, missing/mismatched
 * delta, or no reader carries a min (then the wrapper used the CSV auto-bbox the
 * frontend doesn't have) — so the map degrades to plain grid indices. */
function resolveGrid(config?: BatchRequest): BlockGrid | null {
  const readers = config?.readers;
  if (!readers || readers.length === 0) return null;
  const delta = readers[0]?.delta;
  if (!delta) return null;
  const uniform = readers.every(
    (r) => r.delta && r.delta[0] === delta[0] && r.delta[1] === delta[1] && r.delta[2] === delta[2]
  );
  if (!uniform) return null;
  const withMin = readers.filter((r) => r.min);
  if (withMin.length === 0) return null;
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  for (const r of withMin) {
    for (let ax = 0; ax < 3; ax++) min[ax] = Math.min(min[ax], r.min![ax]);
  }
  return { delta, min };
}

/** Real centroid of a block on one axis from its grid index: `Min + (i + ½)·Δ`
 * (`Modelo::centro`, GeoInnovaLib). */
function centroid(index: number, min: number, delta: number): number {
  return round3(min + (index + 0.5) * delta);
}

// data[0] carries a map {"i,j,k": class} → spatialCategorical points split by
// class, with the level kept as a field. When the block grid resolves, the
// integer indices become real centroid coordinates (Este/Norte/elevation);
// otherwise they stay as grid indices (i/j). Defaults to the first level via a
// categorical filter so the 2D map isn't cluttered; a level selector updates it.
function buildSpatialMap(
  intent: SpatialMapChart,
  rows: unknown[],
  grid: BlockGrid | null,
  algo?: AlgorithmEntry
): IdlessChart | null {
  const row = rows[0] as Record<string, unknown> | undefined;
  const map = (row?.[intent.field] as Record<string, unknown>) ?? {};
  const entries = Object.entries(map);
  if (entries.length === 0) return null;

  // Base subtitle: what the block decision was taken on — the cut-off (cut-off
  // map) or the checked category + its column (category map). Per-bench
  // n/accuracy is appended live by the kit's bench selector. The "categoría de
  // interés" segment (US 2202) is appended last, same "· "-joined shape as
  // cutoffLabels/categoryLabels — undefined unless the algorithm is one
  // `supportsCategoryOfInterest` actually offered the picker for, i.e. today
  // only `map_accuracy_category` among this layout's algorithms (shared with
  // `map_accuracy_cutoff`, which never gets period leaves so this stays a
  // no-op there).
  const cutoff = algo?.parameters?.['cutoff'];
  const categoryCheck = algo?.parameters?.['category_check'];
  let subtitle = intent.subtitle;
  if (typeof cutoff === 'number') {
    subtitle = `Ley de corte = ${cutoff} % CuT`;
  } else if (typeof categoryCheck === 'string' && categoryCheck !== '') {
    const catCol =
      comparedColumnName(algo, 0, 'category_comparison_column_1') ??
      comparedColumnName(algo, 1, 'category_comparison_column_2');
    subtitle = catCol
      ? `Categoría "${categoryCheck}" (columna: ${catCol})`
      : `Categoría "${categoryCheck}"`;
  }
  const categoryOfInterest = categoryOfInterestLabel(algo);
  if (categoryOfInterest) subtitle = [subtitle, categoryOfInterest].filter(Boolean).join(' · ');

  // Class code → readable label (the Q1–Q4 confusion-matrix quadrants: cut-off
  // map uses CUTOFF_MAP_CLASS_LABELS, category map CATEGORY_MAP_CLASS_LABELS).
  // Absent classLabels → the raw code.
  const classLabel = (cls: unknown): string =>
    intent.classLabels?.[Number(cls)] ?? String(cls);

  const data: DataPoint[] = entries.map(([key, cls]) => {
    const [i, j, k] = key.split(',').map(Number);
    return {
      [intent.xKey]: grid ? centroid(i, grid.min[0], grid.delta[0]) : i,
      [intent.yKey]: grid ? centroid(j, grid.min[1], grid.delta[1]) : j,
      [intent.levelKey]: grid ? centroid(k, grid.min[2], grid.delta[2]) : k,
      [intent.groupKey]: classLabel(cls),
    };
  });
  const firstLevel = String(data[0][intent.levelKey]);

  // Without a resolved grid the level values are bare k indices, not metres — so
  // drop the unit and relabel the selector "Nivel (índice)" instead of implying
  // an elevation the map doesn't actually have.
  const options = grid
    ? intent.options
    : {
        ...intent.options,
        levelSelectorLabel: 'Nivel (índice)',
        levelSelectorUnit: undefined,
      };

  return {
    type: intent.type,
    title: intent.title,
    subtitle,
    data,
    encoding: { xKey: intent.xKey, yKey: intent.yKey, groupKey: intent.groupKey },
    ...(intent.groupColors ? { groupColors: intent.groupColors } : {}),
    // Real coords → the intent's Este/Norte labels; grid indices → the raw keys.
    xAxisLabel: grid ? intent.xAxisLabel : intent.xKey,
    yAxisLabel: grid ? intent.yAxisLabel : intent.yKey,
    filters: [{ field: intent.levelKey, kind: 'categorical', values: [firstLevel] }],
    options,
  };
}

// data[0] carries one or more equal-length vectors → one series per vector.
// The x axis uses the real config values (`intent.xParam`, e.g. the cut-off
// list) when resolvable, falling back to the 1-based index. Colors left to the
// palette.
/** Solid black: the tolerance lines are a threshold, not a series. */
const TOLERANCE_LINE_COLOR = '#000000';

/**
 * The `error` chart's tolerance band (SPEC/2423): two dashed black lines at ±t.
 * dashboard-kit dashes every reference line by default, so only the color is set.
 * The user types percent and the Lib returns percent (SPEC/2620), so `t` is
 * plotted as typed, on the scale the axis label "(%)" states.
 */
function toleranceLimits(t: unknown): ChartConfig['limits'] {
  if (typeof t !== 'number' || !Number.isFinite(t) || t <= 0) return undefined;
  return {
    hlines: [
      { value: t, color: TOLERANCE_LINE_COLOR },
      { value: -t, color: TOLERANCE_LINE_COLOR },
    ],
  };
}

function buildIndexedVectors(
  intent: IndexedVectorsChart,
  rows: unknown[],
  algo?: AlgorithmEntry
): IdlessChart | null {
  const row = rows[0] as Record<string, unknown> | undefined;
  // A series whose `preScaledWhen` names a config param but has no config to read
  // it from is DROPPED rather than guessed. `algo` is undefined when the result
  // holds more envelopes of a name than the config has entries — delete the
  // algorithm and save without re-running, and the stored envelope outlives its
  // entry. Guessing scales that series wrong by 100x in one direction or the
  // other, and a wrong number in a client report is worse than a missing curve.
  const drawable = intent.series.filter((s) => !(s.preScaledWhen !== undefined && !algo));
  const series = drawable.map((s) => ({ ...s, values: (row?.[s.field] as number[]) ?? [] }));
  const n = Math.max(0, ...series.map((s) => s.values.length));
  if (n === 0) return null;

  // Real x values from the entered config parameter (same shape as the dual-axis
  // cut-off charts); fall back to the 1-based position when absent or too short.
  const xParamValue = intent.xParam ? algo?.parameters?.[intent.xParam] : undefined;
  const xValues =
    Array.isArray(xParamValue) && xParamValue.length >= n ? (xParamValue as number[]) : undefined;

  const data: DataPoint[] = [];
  for (let i = 0; i < n; i++) {
    const point: DataPoint = { [intent.xKey]: xValues ? xValues[i] : i + 1 };
    series.forEach((s) => {
      // The Lib emits NaN for an undefined error (zero denominator) and it
      // arrives as null: plot a gap. A 0 here would read as a perfect
      // reconciliation, which is the one number a report must not invent.
      const raw = s.values[i];
      if (typeof raw !== 'number' || !Number.isFinite(raw)) {
        point[s.label] = null;
        return;
      }
      point[s.label] = raw;
    });
    data.push(point);
  }
  if (intent.sortByX && xValues) sortByX(data, intent.xKey);

  // The grouping (category) column names the X axis directly — its values are the
  // ticks — so a categorical X reads as "litología", not the generic "Categoría".
  // Falls back to the intent's static label when the column can't be resolved.
  // Only when the ticks ARE those categories: naming the axis after the column
  // while the ticks fell back to 1..n would assert a correspondence nobody
  // checked. The two travel together.
  let xAxisLabel = intent.xAxisLabel;
  if (intent.groupColumn && (xValues || !intent.xParam)) {
    const catCol =
      comparedColumnName(algo, 0, intent.groupColumn[0]) ??
      comparedColumnName(algo, 1, intent.groupColumn[1]);
    if (catCol) xAxisLabel = catCol;
  }
  // Subtitle: the compared LP/CP columns + any applied filter, in the shared
  // "Variables: … (CP) · … (LP)" shape. The grouping column isn't repeated here —
  // it's already the X-axis label. Falls back to the static intent subtitle, and
  // the intent's note (how the figure aggregates) is appended last so it survives
  // either path.
  const resolvedSubtitle = comparedSubtitle(algo, intent.comparedColumns) ?? intent.subtitle;
  const subtitle = [resolvedSubtitle, intent.subtitleNote].filter(Boolean).join(' · ') || undefined;
  // A run executed in accumulative mode (HACK(lib-2165)) carries a Ley series that
  // is already percent from its second point on, next to fractional ones: no one
  // band fits them all, so none is drawn rather than a misleading one.
  const accumulative = algo?.parameters?.is_accumulative_error === true;
  const limits =
    intent.toleranceLines && !accumulative ? toleranceLimits(algo?.error_tolerance) : undefined;

  return {
    type: intent.type,
    title: intent.title,
    subtitle,
    data,
    encoding: { xKey: intent.xKey },
    xAxisLabel,
    yAxisLabel: intent.yAxisLabel,
    series: drawable.map((s) => ({
      dataKey: s.label,
      name: s.label,
      // honor the intent's chart type so bar-shaped algorithms (match_geology)
      // render bars; line-shaped ones (error, accuracy_cutoff) stay lines.
      type: intent.type === 'bar' ? ('bar' as const) : ('line' as const),
      // All three optional on the intent (US 2461) — absent on every series
      // that doesn't set them, which keeps dashboard-kit's default color, no
      // markers, straight (non-spline) segments: `match_geology` sets none, the
      // three `error` charts set markers + smooth but no color (US 2510).
      ...(s.color ? { color: s.color } : {}),
      ...(s.markers ? { markers: s.markers } : {}),
      ...(s.smooth ? { smooth: s.smooth } : {}),
    })),
    options: intent.options,
    ...(limits ? { limits } : {}),
  };
}

function buildChart(
  algorithmName: string,
  intent: ChartIntent,
  rows: unknown[],
  algo?: AlgorithmEntry,
  grid?: BlockGrid | null
): IdlessChart | null {
  switch (intent.layout) {
    case 'paired-arrays':
      return buildPairedArrays(intent, rows, algo);
    case 'models-dual-axis': {
      // The measure selection decides geometry: 3 → stacked panels (panel order),
      // else dual-axis (2 chosen measures in axis order, or the intent default) —
      // which draws a lone chosen measure on a single axis (SPEC/2515).
      const sel = selectedMeasures(algo);
      return sel && sel.length >= 3
        ? buildStackedPanels(algorithmName, intent, rows, algo, orderedMeta(PANEL_ORDER, sel))
        : buildModelsDualAxis(
            algorithmName,
            intent,
            rows,
            algo,
            sel ? orderedMeta(DUAL_AXIS_ORDER, sel) : null
          );
    }
    case 'xy-points':
      return buildXYPoints(intent, rows, algo);
    case 'spatial-map':
      return buildSpatialMap(intent, rows, grid ?? null, algo);
    case 'indexed-vectors':
      return buildIndexedVectors(intent, rows, algo);
  }
}

/**
 * Convert one AlgorithmResultEnvelope into zero or more ChartConfigs. Returns []
 * when the algorithm has no chart intent, or the envelope carries no rows.
 *
 * `instanceId` is the identity of the CONFIGURED CHART this envelope came from —
 * the id its algorithm entry carries (lib/algorithmInstance), never the algorithm
 * name and never a position. Chart ids are `${instanceId}-${idSuffix}`, so
 * switching the tonnage comparison mode (which renames the algorithm) or deleting
 * an earlier sibling of the same name leaves every id byte-identical. Passing
 * `envelope.algorithm` here would restore the old, moving id — hence the required
 * parameter rather than a default.
 *
 * Uniqueness across a dashboard is NOT this function's to guarantee: several
 * envelopes legitimately share one identity (a by-category fan-out lowers one
 * configured chart into N entries carrying its id), and only the walk sees them
 * all. `walkEnvelopeCharts` disambiguates.
 */
export function algorithmResultToCharts(
  envelope: AlgorithmResultEnvelope,
  instanceId: string,
  algo?: AlgorithmEntry,
  grid?: BlockGrid | null
): ChartConfig[] {
  const rows = envelope?.data;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  let intents = CHART_INTENT[envelope.algorithm];
  if (!intents) return [];
  // `error` declares one chart per base (LP/CP/SP); draw the ones the user ticked
  // in `error_bases`, defaulting to a single curve. The id ends in `-error-<base>`
  // whichever bases are drawn, so a chart keeps its identity whatever else is
  // selected alongside it — and `errorBaseSlot` reads that base back out.
  if (envelope.algorithm === 'error') {
    const bases = resolveErrorBases(algo?.error_bases);
    intents = intents.filter((i) => bases.some((b) => i.idSuffix === `error-${b}`));
  }
  // Where a chart id is spelled. `intent` is still in hand here, which is why the
  // stamping lives at this level and not in the six builders. Exactly one other
  // place touches it: walkEnvelopeCharts appends the `-N` repeat suffix.
  return intents.flatMap((intent) => {
    const chart = buildChart(envelope.algorithm, intent, rows, algo, grid);
    return chart ? [{ ...chart, id: `${instanceId}-${intent.idSuffix}` }] : [];
  });
}

/** Index of the Nth algorithm entry named `name` in the input config, or -1.
 *  The index (not the entry) because the caller also reads that position's
 *  instance id, and the two must come from the same entry. */
function nthAlgorithmIndex(
  config: BatchRequest | undefined,
  name: string,
  occurrence: number
): number {
  const algorithms = config?.algorithms;
  if (!algorithms) return -1;
  let count = 0;
  for (let i = 0; i < algorithms.length; i++) {
    if (algorithms[i].name !== name) continue;
    if (count === occurrence) return i;
    count++;
  }
  return -1;
}

/**
 * Assemble a DashboardConfig from a parsed result.json, mapping each
 * envelope it can and skipping the rest. Tolerant of malformed input
 * (returns an empty dashboard rather than throwing).
 *
 * `config` is the executed input request; it lets a chart read its algorithm's
 * parameters (e.g. the scatter's cutoff for the cut-off cross-hair), correlated
 * to each envelope by name + occurrence.
 */
// Single walk over the result's envelopes → the charts each produces, tagged
// with the id of the configured chart it came from. `resultToDashboard` and
// `chartInstanceMap` both consume it, so a chart id and its instance can never
// drift apart.
//
// The shared block grid (from the executed config's readers) recovers the
// spatial maps' real coordinates from their integer i,j,k keys.
//
// TWO counters, deliberately, and neither of them numbers a chart id:
//
//   configOccurrences — advances on EVERY envelope of a name, and is what finds
//     the config entry (`nthAlgorithmIndex`). The result array and the config's
//     algorithms are parallel lists frozen by the same run, so position is a
//     sound correlation here and nowhere else. It used to share the counter
//     below, which meant one envelope that drew nothing made every later
//     envelope of that name read the PREVIOUS entry's parameters.
//   drawnOccurrences — advances only on chart-producing envelopes, and is the sole
//     input to the FALLBACK identity below; keeping it separate is what makes a bare
//     result's identities match the ones a pre-minting config already spells.
//
// The `-N` chart-id suffix used to be a third counter of the same kind, over the
// envelopes that drew within one identity. A split figure is now named by its
// CATEGORY instead (SPLIT_ID_SEPARATOR), and the suffix survives only as a
// collision net — see the note at the push.
/**
 * Separator between a configured chart's identity and the category one of its
 * figures was filtered to, inside a CHART id. Never inside an instance id: identity
 * stays the card's, which is what keeps every consumer of `chartInstanceMap`
 * working. It sits BEFORE the intent suffix so the id still ENDS in that suffix —
 * config/chartIntent's two readers (compatibleTypesFor, errorBaseSlot) anchor there.
 */
const SPLIT_ID_SEPARATOR = '::';

function walkEnvelopeCharts(
  result: ResultJson,
  config?: BatchRequest
): { instanceId: string; category: string | null; charts: ChartConfig[] }[] {
  const envelopes = Array.isArray(result?.results) ? result.results : [];
  const grid = resolveGrid(config);
  // Index-aligned to config.algorithms; materialised for pre-minting configs.
  const instanceIds = executedInstanceIds(config);
  const configOccurrences = new Map<string, number>();
  const drawnOccurrences = new Map<string, number>();
  // Every identity already spoken for: the config's, plus each one `guess` hands
  // out. It has to OUTLIVE the loop and absorb its own guesses — rebuilt per
  // envelope from `instanceIds` alone, a guess that had to skip a claimed id left
  // the next guess starting on the id it had just taken, so two unrelated
  // envelopes answered to one identity and the `-N` below "disambiguated" them as
  // if they were fan-out siblings. Reachable only from ids shaped `name#<integer>`
  // — a config that predates minting — which is why it survived the tests.
  const claimed = new Set(instanceIds);
  const out: { instanceId: string; category: string | null; charts: ChartConfig[]; figureTitle?: string }[] = [];
  const savedTitles = (config?.algorithms ?? []).flatMap((a) =>
    a.figure_title ? [a.figure_title] : []
  );
  const perPrefix = new Map<string, number>();

  for (const envelope of envelopes) {
    const configOccurrence = configOccurrences.get(envelope.algorithm) ?? 0;
    configOccurrences.set(envelope.algorithm, configOccurrence + 1);
    const entryIndex = nthAlgorithmIndex(config, envelope.algorithm, configOccurrence);
    const algo = entryIndex >= 0 ? config?.algorithms?.[entryIndex] : undefined;

    // READ the counter before building, ADVANCE it after: the identity has to be
    // known to build the ids, and only a drawing envelope may consume a slot.
    // Nothing between the two touches the map, so WHICH envelopes consume one is
    // unchanged from when the identity was resolved after the build.
    const drawn = drawnOccurrences.get(envelope.algorithm) ?? 0;
    // No config entry — a bare result, or a result carrying more envelopes of a
    // name than the config has entries — leaves nothing to read an id from, so
    // fall back to the string that position implied, which is the same one a
    // pre-minting reconciliation's stored references already hold.
    //
    // Skipping past ids the config already claims is what makes that guess safe,
    // and it is the same walk `materialise` does on the draft side. Without it,
    // `[chartless, H, H]` over a two-entry config hands the third envelope
    // `histogram#1` — the identity the SECOND one legitimately resolved — and
    // then `instanceIndex` resolves it, so deleting that card prunes both figures
    // and its ⚙ edits a chart it does not own. Landing on a free id instead means
    // the strict resolvers answer -1, which is what the surrounding comment can
    // now honestly promise: a guessed identity goes inert, and ResultDashboard
    // says so instead of presenting dead buttons.
    const guess = (): string => {
      let n = drawn;
      let id = positionalInstanceId(envelope.algorithm, n);
      while (claimed.has(id)) id = positionalInstanceId(envelope.algorithm, ++n);
      return id;
    };
    const instanceId = entryIndex >= 0 ? instanceIds[entryIndex] : guess();

    // A split analysis names its figure by the category it was filtered to, so the
    // chart id survives a sibling being unpicked. An ordinal would not: remove the
    // middle category and the third figure inherits the second's number, and with it
    // the palette and caption the user saved for the second.
    const category = algo ? executedSplitCategory(algo) : null;
    const idPrefix =
      category === null ? instanceId : `${instanceId}${SPLIT_ID_SEPARATOR}${category}`;
    const charts = algorithmResultToCharts(envelope, idPrefix, algo, grid);
    if (charts.length === 0) continue;
    drawnOccurrences.set(envelope.algorithm, drawn + 1);
    // A guessed identity is spoken for only once it DRAWS — same rule as the
    // counter above, and the reason this is not inside `guess`: that runs for every
    // envelope, so claiming there would let a chartless one burn an id and push the
    // next envelope off the identity it should have had. Re-adding one that came
    // from the config is a no-op.
    claimed.add(instanceId);

    // Last-resort net: two analyses reaching here under the SAME prefix would draw
    // byte-identical chart ids — one ChartExportProvider slot for both graph divs and
    // the same PNG twice in the PDF. It counts DRAWN envelopes, which is only sound
    // because it never has to be stable: a prefix repeats when two categories read
    // back to the same label (a column holding both "1" and "1.0" — the reader
    // stores those as one number, so they are one category), or when a config repeats
    // an instance_id with no split filter to tell its entries apart. Both are shapes
    // the picker cannot produce; the net is here so neither becomes a duplicated
    // figure in a client PDF.
    const repeat = perPrefix.get(idPrefix) ?? 0;
    perPrefix.set(idPrefix, repeat + 1);
    out.push({
      instanceId,
      category,
      charts: repeat === 0 ? charts : charts.map((c) => ({ ...c, id: `${c.id}-${repeat + 1}` })),
      // A chart saved with an algorithm's old default name shows the current one
      // (Bug 2732) — same rule the draft applies on load.
      figureTitle: algo?.figure_title
        ? currentFigureTitle(algo.name, algo.figure_title, savedTitles)
        : undefined,
    });
  }

  // Apply the user's figure name (SPEC/2240) as the chart's own title. An
  // instance that draws several figures (the `error` algorithm's LP/CP/SP
  // bases sharing one envelope, or the tonnage-by-category fan-out sharing one
  // instance id across envelopes) numbers them with the same 1-based positional
  // suffix its chart ids already use (`-2`, `-3`, … above) instead of the
  // static per-base/per-category text: the base name stays one, editable in one
  // place, exactly as SPEC/2240 decided for the fan-out case.
  const seenPerInstance = new Map<string, number>();
  return out.map((entry) => {
    const charts = entry.charts.map((c) => {
      const n = (seenPerInstance.get(entry.instanceId) ?? 0) + 1;
      seenPerInstance.set(entry.instanceId, n);
      if (!entry.figureTitle) return c;
      return { ...c, title: n === 1 ? entry.figureTitle! : `${entry.figureTitle} #${n}` };
    });
    return { ...entry, charts };
  });
}

// Uniform card height for the dashboard grid, so heterogeneous charts (a wide
// spatial map vs a narrow histogram) read as one consistent set of tiles rather
// than wildly different sizes. Applied to every chart except stackedPanels,
// whose height scales with its panel count (set in buildStackedPanels). Exported
// so the pre-execution placeholders (PlaceholderCharts) match the executed cards
// and the layout doesn't jump on run.
export const UNIFORM_CARD_HEIGHT = 420;

/** Normalize a chart's card footprint to the shared grid tile: one column
 *  (drop colSpan) and the uniform height (overrides per-type aspect ratio).
 *
 *  EVERY type, with no exceptions. `stackedPanels` used to keep a height computed
 *  from its panel count (~160px each, so each rotated Y-axis title fitted in its own
 *  band); that made its card taller than the rest of the grid, which is the thing a
 *  uniform tile exists to prevent. Its panels now share the same height as everyone
 *  else's single plot, three ways instead of one.
 *
 *  The PLAN VIEW is not an exception either, and that was a decision rather than an
 *  oversight: a taller or wider tile for it grows its ROW-MATES too (the grid sizes a
 *  row by its tallest card), so one chart's need would have been paid for by every
 *  chart beside it. It can never fill its card anyway — it draws metres square, so
 *  the leftover is a property of the deposit's aspect, not of the tile. What the kit
 *  does with that leftover is orient the composition to the deposit's shape and
 *  centre the drawing in what remains (dashboard-kit's makeLayout). Its `colSpan`,
 *  declared in chartIntent, is dropped here like everyone else's. */
function normalizeCardSize(c: ChartConfig): ChartConfig {
  const options = { ...c.options };
  delete options.colSpan;
  options.cardHeight = UNIFORM_CARD_HEIGHT;
  return { ...c, options };
}

export function resultToDashboard(result: ResultJson, config?: BatchRequest): DashboardConfig {
  const charts = walkEnvelopeCharts(result, config).flatMap((e) => e.charts).map(normalizeCardSize);
  return {
    id: 'reconciliation-dashboard',
    title: 'Resultados de reconciliación',
    charts,
  };
}

/**
 * Map every chart id the current result produces to the STABLE id of the
 * configured chart it came from. Shares `resultToDashboard`'s walk, so the keys
 * are exactly the dashboard's chart ids. Charts that share an envelope (an
 * algorithm with several chart intents) share one instance id, and so do the
 * several envelopes of a by-category fan-out.
 *
 * The values survive edits to the draft: that is what keeps an executed chart's
 * edit / duplicate / delete working when a sibling is deleted, instead of going
 * inert until the next run.
 */
export function chartInstanceMap(
  result: ResultJson,
  config?: BatchRequest
): Record<string, string> {
  return chartMaps(result, config).instances;
}

/**
 * chartId → the category that figure was filtered to, for the figures that have one.
 *
 * A configured chart can own several figures: the tonnage category split lowers it to
 * one analysis per category. `chartInstanceMap` says WHICH CHART a figure belongs to;
 * this says WHICH of its figures. Deleting one needs both — it unpicks that category
 * rather than dropping the whole analysis (see useChartInstanceActions).
 *
 * Absent for every chart that is not a split figure.
 */
export function chartCategoryMap(
  result: ResultJson,
  config?: BatchRequest
): Record<string, string> {
  return chartMaps(result, config).categories;
}

/** Both maps off ONE walk, memoised together — see the note below on the cost. */
function chartMaps(result: ResultJson, config?: BatchRequest): ChartMaps {
  const cached = memoisedMaps.get(result)?.get(config ?? NO_CONFIG);
  if (cached) return cached;

  const maps: ChartMaps = { instances: {}, categories: {} };
  for (const { instanceId, category, charts } of walkEnvelopeCharts(result, config)) {
    for (const chart of charts) {
      maps.instances[chart.id] = instanceId;
      if (category !== null) maps.categories[chart.id] = category;
    }
  }

  const byConfig = memoisedMaps.get(result) ?? new Map<object, ChartMaps>();
  byConfig.set(config ?? NO_CONFIG, maps);
  memoisedMaps.set(result, byConfig);
  return maps;
}

interface ChartMaps {
  /** chartId → the identity of the configured chart that produced it. */
  instances: Record<string, string>;
  /** chartId → the category it was filtered to, for split figures only. */
  categories: Record<string, string>;
}

/**
 * The walk BUILDS every chart — data arrays and all — and this map throws all of
 * that away but the ids, which measured ~99 ms for four 20k-block spatial maps.
 * Three live callers ask for it (the dashboard, the sidebar tree, the config
 * modal), each with its own `useMemo`, so the same result was walked three times
 * per execution or reconciliation open.
 *
 * `result` and `config` are REPLACED wholesale rather than mutated — every write
 * to `current` assigns the object the API returned (store/useStore) — so object
 * identity tracks content and is a sound cache key, and a WeakMap lets a closed
 * reconciliation's entry be collected. Not literally frozen: mutating a config in
 * place WOULD serve a stale map, which is why "replace, never mutate" is the
 * invariant to keep rather than an accident of how it happens to work today.
 * Keyed on BOTH because the same result is legitimately mapped with and without a
 * config (a bare result yields fallback ids).
 */
const memoisedMaps = new WeakMap<ResultJson, Map<object, ChartMaps>>();
/** Stand-in key for "no config" — WeakMap-friendly and never collides. */
const NO_CONFIG: object = Object.freeze({});
